<!--
  字幕编辑器页面包装组件
  处理路由参数并传递给 EnhancedVideoSubtitleEditor
-->
<template>
  <div class="subtitle-editor-page">
    <!-- 加载状态（包括项目加载和字幕提取） -->
    <div v-if="loading" class="loading-container">
      <a-spin size="large" :tip="t('video.subtitleEditor.loadingProjectData')">
        <div class="loading-placeholder"></div>
      </a-spin>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <a-result
        status="error"
        :title="t('video.subtitleEditor.loadFailed')"
        :sub-title="error"
      >
        <template #extra>
          <a-space>
            <a-button type="primary" @click="loadProject">{{ t('common.retry') }}</a-button>
            <a-button @click="$router.back()">{{ t('common.back') }}</a-button>
          </a-space>
        </template>
      </a-result>
    </div>

    <!-- 主要内容 -->
    <div v-else-if="project" class="main-editor">
      <EnhancedSubtitleEditor
        :project-id="projectId"
        :project="project"
        :subtitles="subtitles"
        :video-url="videoUrl"
        :current-time="currentTime"
        :available-languages="availableLanguages"
        @subtitle-click="handleSubtitleClick"
        @subtitle-update="handleSubtitleUpdate"
        @subtitle-create="handleSubtitleCreate"
        @subtitle-delete="handleSubtitleDelete"
        @subtitles-merge="handleSubtitlesMerge"
        @subtitle-split="handleSubtitleSplit"
        @batch-operation="handleBatchOperation"
        @save-project="handleSave"
        @workflow-next-step="handleWorkflowNextStep"
      />
    </div>
    
    <!-- 备用编辑器（当项目数据加载失败时） -->
    <div v-else class="fallback-editor">
      <a-alert
        :message="t('video.subtitleEditor.useFallbackEditor')"
        :description="t('video.subtitleEditor.fallbackEditorDescription')"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      />
      <EnhancedSubtitleEditor
        :project-id="projectId"
        :subtitles="[]"
        :current-time="0"
        :show-translation="true"
        :available-languages="availableLanguages"
        @subtitle-click="handleSubtitleClick"
        @subtitle-update="handleSubtitleUpdate"
        @subtitle-create="handleSubtitleCreate"
        @subtitle-delete="handleSubtitleDelete"
        @subtitles-merge="handleSubtitlesMerge"
        @subtitle-split="handleSubtitleSplit"
        @batch-operation="handleBatchOperation"
        @save-project="handleSave"
        @workflow-next-step="handleWorkflowNextStep"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import EnhancedSubtitleEditor from '@/modules/video/components/EnhancedSubtitleEditor.vue'
import { getWorkItem, type WorkItem } from '@/modules/project/api'
import { subtitleApi, type Subtitle } from '@/modules/video/api'
import { getCorrectedApiOrigin } from '@/shared/api/api-base'
import { runApiBatch } from '@/shared/api/use-api-batch'
import { videoPlaybackUrl } from '@/modules/video/utils/video-playback-url'

/** 路由 :id 是 WorkItem；兼容条目上可能仍返回的视频路径字段 */
type SubtitleEditorItem = WorkItem & {
  video_file_path?: string | null
}

const { t } = useI18n()

// 路由参数
const route = useRoute()
const router = useRouter()
const projectId = Number(route.params.id)

// 响应式状态
const loading = ref(true)
const error = ref('')
const project = ref<SubtitleEditorItem | null>(null)
const subtitles = ref<Subtitle[]>([])
const currentTime = ref(0)
const availableLanguages = ref(['zh', 'en', 'ja', 'ko'])
const extractionStatus = ref<any>(null)
const isExtracting = ref(false)
let extractionPollInterval: number | null = null
let lastSubtitleCheckTime: number | null = null // 记录最后检查字幕数据的时间
const SUBTITLE_CHECK_INTERVAL = 10 * 1000 // 每10秒检查一次字幕数据

// 计算视频URL（优先使用 file_storage_id）
const videoUrl = computed(() => {
  const proxyUrl = videoPlaybackUrl({
    video_file_storage_id: project.value?.video_file_storage_id,
    file_storage_id: project.value?.file_storage_id,
  })
  if (proxyUrl) return proxyUrl
  if (project.value?.file_url) {
    return buildVideoUrl(project.value.file_url)
  }
  return ''
})

// 构建视频URL
const buildVideoUrl = (fileUrl: string) => {
  if (!fileUrl) return ''

  // 统一路径分隔符为正斜杠
  const normalizedUrl = fileUrl.replace(/\\/g, '/')

  // 规范 /api/v1/ 路径（非 stream 端点）：补全 origin 后返回
  if (normalizedUrl.startsWith('/api/v1/')) {
    const baseUrl = getCorrectedApiOrigin() || window.location.origin
    return `${baseUrl}${normalizedUrl}`
  }

  // 如果是相对路径，转换为API端点
  if (normalizedUrl.startsWith('uploads/')) {
    // 提取项目ID和文件名（支持中文等特殊字符，需编码）
    const pathParts = normalizedUrl.split('/')
    if (pathParts.length >= 4) {
      const filename = pathParts[3]
      const fullUrl = `${getCorrectedApiOrigin() || window.location.origin}/api/v1/videos/stream/video/${projectId}/${encodeURIComponent(filename)}`
      return fullUrl
    }
  }
  
  // 如果已经是完整URL，直接返回
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return normalizedUrl
  }
  
  return fileUrl
}

// 检查字幕提取状态
const checkExtractionStatus = async () => {
  try {
    const progressData = await subtitleApi.getExtractionProgress(projectId)
    
    // 调试日志：完整输出API响应
    console.log('🔍 字幕提取进度检查:', {
      progressData,
      hasExtractionProgress: !!progressData?.extraction_progress,
      extractionProgress: progressData?.extraction_progress
    })
    
    if (progressData?.extraction_progress) {
      extractionStatus.value = progressData.extraction_progress
      const step = progressData.extraction_progress.step
      
      console.log('📊 提取步骤详情:', {
        step,
        progress: progressData.extraction_progress.progress,
        message: progressData.extraction_progress.message,
        subtitleCount: progressData.extraction_progress.subtitle_count,
        currentSubtitlesCount: subtitles.value?.length || 0
      })
      
      // 关键修复：如果已有字幕，即使 step 是中间状态，也应该停止轮询
      if (subtitles.value && subtitles.value.length > 0) {
        console.log('✅ 检测到已有字幕数据，停止提取状态检查')
        isExtracting.value = false
        return false // 停止轮询
      }
      
      // 检查是否正在提取中
      if (step === 'extracting_audio' || step === 'audio_extracted' || 
          step === 'transcribing' || step === 'processing_subtitles') {
        
        // 关键修复：即使后端返回的是中间状态，也检查是否有字幕数据
        // 因为可能存在后端已完成但进度未及时更新的情况
        // 限制检查频率，每10秒检查一次，避免频繁API调用
        const now = Date.now()
        const shouldCheckSubtitles = !lastSubtitleCheckTime || (now - lastSubtitleCheckTime) >= SUBTITLE_CHECK_INTERVAL
        
        if (shouldCheckSubtitles && (!subtitles.value || subtitles.value.length === 0)) {
          lastSubtitleCheckTime = now
          // 重新加载字幕数据，检查是否后端已完成但进度未更新
          try {
            const subtitlesData = await subtitleApi.getProjectSubtitles(projectId)
            if (subtitlesData && subtitlesData.length > 0) {
              console.log('✅ 检测到字幕数据（后端可能已完成但进度未更新），停止轮询')
              subtitles.value = [...subtitlesData]
              isExtracting.value = false
              loading.value = false
              stopExtractionPolling()
              message.success(t('video.subtitleEditor.extractionCompleted', { count: subtitlesData.length }))
              return false
            }
          } catch (err) {
            console.warn('检查字幕数据失败:', err)
            // 继续正常的进度检查流程
          }
        }
        
        isExtracting.value = true
        return true // 继续轮询
      } else if (step === 'completed') {
        // 提取完成，停止轮询并刷新字幕
        console.log('✅ 检测到字幕提取完成状态，开始刷新数据...')
        stopExtractionPolling()
        
        // 重新加载项目数据和字幕（确保数据最新）
        try {
          // 停止提取状态，保持 loading 状态以便显示加载界面
          console.log('🔄 更新状态: isExtracting = false, loading = true')
          isExtracting.value = false
          loading.value = true
          
          // 等待一小段时间，确保数据库写入完成
          await new Promise(resolve => setTimeout(resolve, 500))
          
          // 重新加载数据
          console.log('📥 开始重新加载项目数据和字幕...')
          await loadProjectData()
          
          const subtitleCount = subtitles.value?.length || 0
          console.log('✅ 字幕提取完成，数据已重新加载:', {
            subtitlesCount: subtitleCount,
            project: project.value,
            hasProject: !!project.value,
            isExtracting: isExtracting.value,
            loading: loading.value
          })
          
          // 如果字幕数据为空，再等待一段时间后重试（数据库可能还在写入）
          if (subtitleCount === 0) {
            console.warn('⚠️ 字幕数据为空，等待后重试加载...')
            await new Promise(resolve => setTimeout(resolve, 1000))
            await loadProjectData()
            const retryCount = subtitles.value?.length || 0
            console.log('🔄 重试加载后的字幕数据:', retryCount)
            
            if (retryCount === 0) {
              console.error('❌ 重试后仍无法加载字幕数据')
            }
          }
          
          // 确保响应式更新
          await nextTick()
          
          // 更新状态：完成加载，显示编辑器
          console.log('🎉 更新状态: loading = false，准备显示编辑器')
          loading.value = false
          
          // 再次等待 DOM 更新
          await nextTick()
          
          // 验证最终状态
          const finalCount = subtitles.value?.length || 0
          console.log('📊 最终状态检查:', {
            subtitlesCount: finalCount,
            isExtracting: isExtracting.value,
            loading: loading.value,
            hasProject: !!project.value
          })
          
          // 显示成功消息
          if (finalCount > 0) {
            message.success(t('video.subtitleEditor.extractionCompleted', { count: finalCount }))
          } else {
            message.warning(t('video.subtitleEditor.extractionCompletedNoData'))
          }
        } catch (err: any) {
          console.error('❌ 重新加载项目数据失败:', err)
          isExtracting.value = false
          loading.value = false
        }
        
        return false // 停止轮询
      } else if (step === 'failed') {
        // 提取失败，停止轮询并显示错误
        isExtracting.value = false
        loading.value = false
        stopExtractionPolling()
        
        // 检查是否是用量不足错误，使用i18n错误消息
        const errorCode = progressData.extraction_progress.error_code
        const rawError = progressData.extraction_progress.error || progressData.extraction_progress.message
        
        console.log('❌ 字幕提取失败:', {
          errorCode,
          rawError,
          extractionProgress: progressData.extraction_progress
        })
        
        let errorMessage = ''
        
        // 优先检查错误代码，使用i18n错误消息
        // 支持多种错误代码格式：'QUOTA_INSUFFICIENT', 'Q002', 'quota_insufficient'
        const normalizedErrorCode = errorCode?.toUpperCase()
        if (normalizedErrorCode === 'QUOTA_INSUFFICIENT' || normalizedErrorCode === 'Q002') {
          // 使用i18n的用量不足错误消息
          errorMessage = t('error.quotaInsufficient')
          console.log('✅ 使用i18n用量不足错误消息:', errorMessage)
        } else if (rawError) {
          // 如果没有错误代码，但错误消息中包含用量相关关键词，也使用i18n
          const quotaKeywords = ['用量', 'quota', '配额', 'insufficient']
          const hasQuotaKeyword = quotaKeywords.some(keyword => 
            rawError.toLowerCase().includes(keyword.toLowerCase())
          )
          
          if (hasQuotaKeyword) {
            errorMessage = t('error.quotaInsufficient')
            console.log('✅ 检测到用量相关关键词，使用i18n错误消息:', errorMessage)
          } else {
            // 其他错误，使用原始错误消息（但也要国际化）
            errorMessage = t('video.subtitleEditor.extractionFailed', { error: rawError })
            console.log('⚠️ 使用原始错误消息（可能包含中文）:', errorMessage)
          }
        } else {
          errorMessage = t('video.subtitleEditor.extractionFailed', { error: t('common.unknownError') })
        }
        
        error.value = errorMessage
        message.error(errorMessage)
        return false // 停止轮询
      }
    }
    
    // 如果没有提取进度信息，检查是否已有字幕
    // 如果没有字幕且项目状态是PROCESSING，可能正在提取中
    if (!progressData?.extraction_progress) {
      console.log('⚠️ 没有提取进度信息，检查项目状态和字幕数据')
      
      // 如果已经有字幕，说明提取已完成，停止轮询
      if (subtitles.value && subtitles.value.length > 0) {
        console.log('✅ 已有字幕数据，停止轮询')
        isExtracting.value = false
        return false // 停止轮询
      }
      
      // 如果没有字幕且项目状态是PROCESSING，可能正在提取中
      if (project.value?.pipeline_stage === 'machine_running') {
        console.log('📝 条目标记为 machine_running 且无字幕，可能正在提取中，继续轮询')
        isExtracting.value = true
        // 保持 loading 状态，显示加载页面
        loading.value = true
        return true // 继续轮询
      }
      
      // 其他情况，停止轮询
      console.log('ℹ️ 没有提取进度且项目状态不是PROCESSING，停止轮询')
      isExtracting.value = false
      loading.value = false
      return false // 停止轮询
    }
    
    // 如果提取进度存在但步骤未知，检查字幕数量
    const step = progressData.extraction_progress.step
    if (!step || step === 'not_started') {
      console.log('📝 提取步骤未知或未开始，检查字幕数据')
      if (subtitles.value && subtitles.value.length > 0) {
        console.log('✅ 已有字幕数据，停止轮询')
        isExtracting.value = false
        return false // 停止轮询
      }
    }
    
    isExtracting.value = false
    return false // 停止轮询
  } catch (err: any) {
    console.error('检查字幕提取状态失败:', err)
    // 如果API调用失败，不影响页面显示
    isExtracting.value = false
    return false
  }
}

// 开始轮询提取进度
const startExtractionPolling = (skipInitialCheck: boolean = false) => {
  if (extractionPollInterval) {
    return // 已经在轮询中
  }
  
  // 如果不是跳过初始检查，立即检查一次（用于独立调用场景）
  if (!skipInitialCheck) {
    checkExtractionStatus()
  }
  
  // 每3秒轮询一次
  extractionPollInterval = window.setInterval(async () => {
    const shouldContinue = await checkExtractionStatus()
    if (!shouldContinue) {
      stopExtractionPolling()
    }
  }, 3000)
}

// 停止轮询
const stopExtractionPolling = () => {
  if (extractionPollInterval) {
    clearInterval(extractionPollInterval)
    extractionPollInterval = null
  }
  // 重置时间记录
  lastSubtitleCheckTime = null
}

// 获取步骤索引（用于显示进度步骤）
const getStepIndex = (step: string): number => {
  const stepMap: Record<string, number> = {
    'extracting_audio': 0,
    'audio_extracted': 0,
    'transcribing': 1,
    'processing_subtitles': 2,
    'completed': 3,
    'failed': -1
  }
  return stepMap[step] ?? 0
}

// 加载项目数据（不包含字幕提取状态检查）
const loadProjectData = async () => {
  try {
    console.log('📥 loadProjectData: 开始加载数据...')

    const results = await runApiBatch([
      (ctx) => getWorkItem(projectId, ctx.requestConfig),
      (ctx) => subtitleApi.getProjectSubtitles(projectId, undefined, ctx.requestConfig),
    ])
    if (results[0]?.status === 'rejected') {
      throw results[0].reason
    }
    if (results[1]?.status === 'rejected') {
      throw results[1].reason
    }
    const projectData = results[0].value as SubtitleEditorItem
    const subtitlesData = results[1].value as Subtitle[]
    
    console.log('📥 loadProjectData: API响应:', {
      projectData: !!projectData,
      subtitlesDataLength: subtitlesData?.length || 0
    })
    
    // 使用响应式更新，确保 Vue 检测到变化
    // 创建新对象和数组，确保引用变化触发响应式更新
    project.value = { ...projectData }
    subtitles.value = subtitlesData ? [...subtitlesData] : []
    
    // 调试信息
    console.log('✅ loadProjectData: 数据已更新:', {
      projectId: project.value?.id,
      subtitlesCount: subtitles.value?.length,
      videoUrl: videoUrl.value
    })
    
    // 确保响应式更新
    await nextTick()
    
    console.log('✅ loadProjectData: nextTick 完成，数据应该已更新')
    
  } catch (err: any) {
    console.error('❌ loadProjectData: 加载失败:', err)
    throw err
  }
}

// 加载项目数据（包含字幕提取状态检查）
const loadProject = async () => {
  try {
    loading.value = true
    error.value = ''
    
    await loadProjectData()
    
    // 关键修复：如果已有字幕，直接显示编辑器，不检查提取状态
    if (subtitles.value && subtitles.value.length > 0) {
      console.log('✅ 项目已有字幕，跳过提取状态检查，直接显示编辑器')
      isExtracting.value = false
      loading.value = false
      return
    }
    
    // 只有在没有字幕的情况下，才检查是否正在提取字幕
    console.log('📝 项目无字幕，检查提取状态...')
    await checkExtractionStatus()
    
    // 如果正在提取，保持 loading 状态并开始轮询（后台轮询，不显示提取进度页面）
    if (isExtracting.value) {
      console.log('📝 检测到正在提取字幕，保持加载状态并开始后台轮询...')
      // 保持 loading.value = true，不设置 isExtracting（已移除UI）
      startExtractionPolling(true) // 传入 true 跳过初始检查，避免重复调用
      // 注意：不要在这里设置 loading.value = false，让轮询完成后自动设置
    } else {
      // 如果不在提取中，确保 loading 状态为 false
      loading.value = false
    }
    
  } catch (err: any) {
    console.error('Failed to load project:', err)
    console.error('Error details:', {
      message: err.message,
      status: err.status,
      response: err.response,
      stack: err.stack
    })
    error.value = err.message || t('video.subtitleEditor.loadProjectFailed')
    loading.value = false
  }
  // 注意：如果正在提取中，不要在 finally 中设置 loading = false
  // 让轮询完成后自动设置
}

// 事件处理
const handleSubtitleClick = (subtitle: Subtitle) => {
  console.log('字幕点击:', subtitle)
  // 可以在这里处理字幕点击事件，比如跳转到对应时间
}

const handleSubtitleUpdate = (subtitle: Subtitle) => {
  console.log('字幕更新:', subtitle)
  const index = subtitles.value.findIndex(s => s.id === subtitle.id)
  if (index !== -1) {
    subtitles.value[index] = subtitle
  }
}

const handleSubtitleCreate = (subtitle: Partial<Subtitle>) => {
  console.log('创建字幕:', subtitle)
  
  // 生成新的字幕ID（使用时间戳作为临时ID）
  const newId = Date.now()
  
  // 创建完整的新字幕对象
  const newSubtitle: Subtitle = {
    id: newId,
    project_id: subtitle.project_id || projectId,
    start_time: subtitle.start_time || 0,
    end_time: subtitle.end_time || 5,
    original_text: subtitle.original_text || '',
    translated_text: subtitle.translated_text || '',
    language: subtitle.language || 'zh',
    order_index: subtitle.order_index || subtitles.value.length,
    is_edited: false,
    modification_type: 'added',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
  
  // 添加到字幕列表
  subtitles.value.push(newSubtitle)
  
  console.log('字幕已添加:', newSubtitle)
}

const handleSubtitleDelete = (subtitleId: number) => {
  console.log('删除字幕:', subtitleId)
  const index = subtitles.value.findIndex(s => s.id === subtitleId)
  if (index !== -1) {
    subtitles.value.splice(index, 1)
  }
}

const handleSubtitlesMerge = async (subtitleIds: number[]) => {
  console.log('合并字幕:', subtitleIds)
  
  if (subtitleIds.length < 2) {
    message.error(t('video.subtitleEditor.mergeAtLeastTwo'))
    return
  }
  
  try {
    // 使用后端专门的合并API
    const result = await subtitleApi.mergeSubtitles(subtitleIds)
    
    if (result) {
      // 删除原来的字幕
      subtitleIds.forEach(id => {
        const index = subtitles.value.findIndex(s => s.id === id)
        if (index !== -1) {
          subtitles.value.splice(index, 1)
        }
      })
      
      // 添加合并后的字幕
      subtitles.value.push(result)
      
      // 重新排序
      subtitles.value.sort((a, b) => a.start_time - b.start_time)
      
      message.success(t('video.subtitleEditor.mergeSuccess', { count: subtitleIds.length }))
    } else {
      message.error(t('video.subtitleEditor.mergeFailedFormatError'))
    }
    
  } catch (error: any) {
    console.error('合并字幕失败:', error)
  }
}

const handleSubtitleSplit = async (subtitleId: number, splitData: any, firstText?: string, secondText?: string) => {
  console.log('拆分字幕:', subtitleId, splitData)
  
  try {
    // 检查参数格式，支持两种格式：
    // 1. 旧格式：handleSubtitleSplit(subtitleId, splitTime, firstText, secondText)
    // 2. 新格式：handleSubtitleSplit(subtitleId, { first_text, split_points })
    
    let splitTime: number
    let finalFirstText: string
    let finalSecondText: string
    
    if (typeof splitData === 'number') {
      // 旧格式：splitData 是 splitTime
      splitTime = splitData
      finalFirstText = firstText || ''
      finalSecondText = secondText || ''
    } else if (splitData && typeof splitData === 'object') {
      // 新格式：splitData 是对象
      if (splitData.split_points && splitData.split_points.length > 0) {
        // 多条拆分
        return await handleSubtitleSplitMultiple(subtitleId, splitData.first_text, splitData.split_points)
      } else {
        // 单条拆分
        splitTime = splitData.split_time || 0
        finalFirstText = splitData.first_text || ''
        finalSecondText = splitData.second_text || ''
      }
    } else {
      message.error(t('video.subtitleEditor.splitParamError'))
      return
    }
    
    // 使用后端专门的拆分API，传递自定义文本
    const result = await subtitleApi.splitSubtitle(subtitleId, splitTime, finalFirstText, finalSecondText)
    
    if (result && result.length === 2) {
      // 找到原字幕并替换为拆分后的两个字幕
      const subtitleIndex = subtitles.value.findIndex(s => s.id === subtitleId)
      if (subtitleIndex !== -1) {
        // 删除原字幕
        subtitles.value.splice(subtitleIndex, 1)
        
        // 添加拆分后的两个字幕
        subtitles.value.push(...result)
        
        // 重新排序
        subtitles.value.sort((a, b) => a.start_time - b.start_time)
      }
      
      message.success(t('video.subtitleEditor.splitSuccess'))
    } else {
      message.error(t('video.subtitleEditor.splitFailedFormatError'))
    }
    
  } catch (error: any) {
    console.error('拆分字幕失败:', error)
  }
}

// 处理多条拆分
const handleSubtitleSplitMultiple = async (subtitleId: number, firstText: string, splitPoints: Array<{ time: number; text: string }>) => {
  console.log('多条拆分字幕:', subtitleId, firstText, splitPoints)
  
  try {
    // 使用后端的多条拆分API
    const result = await subtitleApi.splitSubtitleMultiple(subtitleId, firstText, splitPoints)
    
    if (result && result.length > 0) {
      // 找到原字幕并替换为拆分后的字幕
      const subtitleIndex = subtitles.value.findIndex(s => s.id === subtitleId)
      if (subtitleIndex !== -1) {
        // 删除原字幕
        subtitles.value.splice(subtitleIndex, 1)
        
        // 添加拆分后的字幕
        subtitles.value.push(...result)
        
        // 重新排序
        subtitles.value.sort((a, b) => a.start_time - b.start_time)
      }
      
      message.success(t('video.subtitleEditor.splitMultipleSuccess', { count: result.length }))
    } else {
      message.error(t('video.subtitleEditor.splitFailedFormatError'))
    }
    
  } catch (error: any) {
    console.error('多条拆分字幕失败:', error)
  }
}

const handleBatchOperation = async (operation: any) => {
  console.log('批量操作:', operation)
  
  try {
    if (operation.operation === 'replace') {
      // 批量替换功能
      if (!operation.search_text || !operation.replace_text) {
        message.error(t('video.subtitleEditor.fillSearchAndReplace'))
        return
      }
      
      let replacedCount = 0
      subtitles.value.forEach(subtitle => {
        if (subtitle.original_text && subtitle.original_text.includes(operation.search_text)) {
          subtitle.original_text = subtitle.original_text.replace(
            new RegExp(operation.search_text, 'g'), 
            operation.replace_text
          )
          subtitle.is_edited = true
          replacedCount++
        }
      })
      
      if (replacedCount > 0) {
        message.success(t('video.subtitleEditor.batchReplaceSuccess', { count: replacedCount }))
      } else {
        message.warning(t('video.subtitleEditor.noMatchFound'))
      }
      
    } else if (operation.operation === 'delete') {
      // 批量删除功能 - 删除字幕中包含关键字的文字内容
      if (!operation.delete_text) {
        message.error(t('video.subtitleEditor.enterDeleteText'))
        return
      }
      
      let modifiedCount = 0
      subtitles.value.forEach(subtitle => {
        if (subtitle.original_text && subtitle.original_text.includes(operation.delete_text)) {
          const originalText = subtitle.original_text
          subtitle.original_text = subtitle.original_text.replace(
            new RegExp(operation.delete_text, 'g'), 
            ''
          )
          // 清理多余的空格
          subtitle.original_text = subtitle.original_text.replace(/\s+/g, ' ').trim()
          
          if (subtitle.original_text !== originalText) {
            subtitle.is_edited = true
            modifiedCount++
          }
        }
      })
      
      if (modifiedCount > 0) {
        message.success(t('video.subtitleEditor.batchDeleteSuccess', { count: modifiedCount }))
      } else {
        message.warning(t('video.subtitleEditor.noKeywordFound'))
      }
    }
    
  } catch (error: any) {
    console.error('批量操作失败:', error)
  }
}

const handleSave = async () => {
  try {
    await subtitleApi.updateProjectSubtitles(projectId, subtitles.value)
  } catch (err: any) {
    console.error('Failed to save subtitles:', err)
  }
}

const handleWorkflowNextStep = () => {
  console.log('进入工作流下一步：跳转到翻译校验页面')
  // 项目状态更新已由后端任务指派接口处理，此处直接跳转
  router.push(`/video/translation-review/${projectId}`)
}

// 生命周期
onMounted(() => {
  loadProject()
})

onUnmounted(() => {
  // 组件卸载时停止轮询
  stopExtractionPolling()
})
</script>

<style lang="less" scoped>
.subtitle-editor-page {
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .loading-placeholder {
    width: 200px;
    height: 200px;
    background: #f5f5f5;
    border-radius: 8px;
  }
}

.error-container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.extraction-waiting-container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  
  .extraction-progress-info {
    width: 100%;
    max-width: 600px;
    
    .progress-steps {
      margin-top: 24px;
    }
    
    .error-message {
      margin-top: 16px;
    }
  }
}

.main-editor {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 确保 flex 子元素可以正确收缩 */
}

.fallback-editor {
  height: 100vh;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 确保 flex 子元素可以正确收缩 */
}
</style>

